import React, { useEffect, useState } from 'react';
import { Link, useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Sparkles, ArrowLeft, ArrowRight, Mail, Lock, User, Phone, Shield, Loader2, Eye, EyeOff, CheckCircle2,
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent } from '../components/ui/card';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const TOKEN_KEY = 'portal_token';
const USER_KEY = 'portal_user';

const authApi = {
  login: (payload) => axios.post(`${API_URL}/api/portal/login`, payload),
  register: (payload) => axios.post(`${API_URL}/api/portal/register`, payload),
  forgot: (payload) => axios.post(`${API_URL}/api/portal/password/forgot`, payload),
  reset: (payload) => axios.post(`${API_URL}/api/portal/password/reset`, payload),
};

const persistSession = (token, user) => {
  localStorage.setItem(TOKEN_KEY, token);
  localStorage.setItem(USER_KEY, JSON.stringify(user));
};

const Shell = ({ title, subtitle, children, data_testid }) => (
  <div className="min-h-[100dvh] bg-[#0a1628] text-slate-100 antialiased relative overflow-hidden flex flex-col safe-top safe-bottom" data-testid={data_testid}>
    <div className="fixed inset-0 bg-gradient-to-br from-[#0a1628] via-[#0d1f3c] to-[#0a0f1c] pointer-events-none" />
    <div className="fixed top-1/4 -left-20 w-96 h-96 bg-sky-500/10 rounded-full blur-[100px] pointer-events-none" />
    <div className="fixed bottom-1/4 -right-20 w-96 h-96 bg-cyan-500/10 rounded-full blur-[100px] pointer-events-none" />
    <div
      className="fixed inset-0 opacity-30 pointer-events-none"
      style={{
        backgroundImage: `linear-gradient(rgba(255,255,255,.06) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,.06) 1px, transparent 1px)`,
        backgroundSize: '40px 40px',
        maskImage: 'radial-gradient(ellipse at center, black 20%, transparent 70%)',
        WebkitMaskImage: 'radial-gradient(ellipse at center, black 20%, transparent 70%)',
      }}
    />
    <header className="relative w-full max-w-5xl mx-auto px-4 sm:px-6 h-16 mt-2 flex items-center justify-between shrink-0">
      <Link to="/" className="flex items-center gap-3" data-testid="home-link">
        <div className="flex items-center justify-center w-10 h-10 rounded-lg bg-sky-500/20 border border-sky-500/30">
          <Sparkles className="w-5 h-5 text-sky-400" />
        </div>
        <div className="leading-tight">
          <div className="font-bold text-sm sm:text-base">F.lli Schilirò Servizi</div>
          <div className="text-[10px] sm:text-xs text-sky-300/70 -mt-0.5">Area Clienti</div>
        </div>
      </Link>
      <Link
        to="/accedi"
        className="text-xs sm:text-sm px-3 py-1.5 rounded-full border border-white/10 hover:border-sky-400/30 hover:bg-sky-500/5 transition-colors inline-flex items-center gap-1.5"
      >
        <ArrowLeft className="w-3.5 h-3.5" />
        Area Riservata
      </Link>
    </header>
    <main className="relative flex-1 w-full max-w-md mx-auto px-4 sm:px-6 py-8 flex flex-col justify-center">
      <div className="text-center mb-7">
        <h1 className="text-3xl sm:text-4xl font-extrabold tracking-tight leading-tight">{title}</h1>
        {subtitle && <p className="mt-2 text-sm text-slate-400">{subtitle}</p>}
      </div>
      <Card className="bg-slate-900/70 border-white/10 backdrop-blur">
        <CardContent className="p-6 sm:p-8">{children}</CardContent>
      </Card>
    </main>
  </div>
);

const InputIcon = ({ Icon, ...rest }) => (
  <div className="relative mt-1">
    <Icon className="w-4 h-4 text-slate-500 absolute left-3 top-1/2 -translate-y-1/2" />
    <Input {...rest} className={`pl-9 bg-slate-950/50 border-white/10 text-white placeholder:text-slate-500 ${rest.className || ''}`} />
  </div>
);

// --------- LOGIN ---------
export const PortalLogin = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPwd, setShowPwd] = useState(false);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    if (localStorage.getItem(TOKEN_KEY)) navigate('/clienti', { replace: true });
  }, [navigate]);

  const submit = async (e) => {
    e.preventDefault();
    if (!email || !password) return;
    setLoading(true);
    try {
      const res = await authApi.login({ email, password });
      persistSession(res.data.token, res.data.user);
      toast.success(`Bentornato, ${res.data.user.nome}`);
      navigate('/clienti', { replace: true });
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Credenziali non valide');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Shell title="Accedi" subtitle="Benvenuto nella tua Area Clienti" data_testid="portal-login-page">
      <form onSubmit={submit} className="space-y-4">
        <div>
          <Label className="text-slate-300 text-sm" htmlFor="email">Email</Label>
          <InputIcon Icon={Mail} id="email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} data-testid="login-email" placeholder="tu@example.com" required />
        </div>
        <div>
          <Label className="text-slate-300 text-sm" htmlFor="pwd">Password</Label>
          <div className="relative mt-1">
            <Lock className="w-4 h-4 text-slate-500 absolute left-3 top-1/2 -translate-y-1/2" />
            <Input id="pwd" type={showPwd ? 'text' : 'password'} value={password} onChange={(e) => setPassword(e.target.value)} data-testid="login-password"
              className="pl-9 pr-10 bg-slate-950/50 border-white/10 text-white placeholder:text-slate-500" required />
            <button type="button" onClick={() => setShowPwd(!showPwd)} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500 hover:text-slate-200">
              {showPwd ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
            </button>
          </div>
        </div>
        <div className="text-right">
          <Link to="/clienti/password-dimenticata" className="text-xs text-sky-300 hover:text-sky-200 underline" data-testid="forgot-link">
            Password dimenticata?
          </Link>
        </div>
        <Button type="submit" disabled={loading} data-testid="login-submit"
          className="w-full h-11 bg-sky-500 hover:bg-sky-400 text-white font-semibold">
          {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : (<>Accedi <ArrowRight className="w-4 h-4 ml-2" /></>)}
        </Button>
        <div className="text-center text-sm text-slate-400">
          Non hai un account?{' '}
          <Link to="/clienti/registrati" className="text-sky-300 underline" data-testid="go-register">Registrati gratis</Link>
        </div>
      </form>
    </Shell>
  );
};

// --------- REGISTER ---------
export const PortalRegister = () => {
  const [form, setForm] = useState({ nome: '', cognome: '', email: '', telefono: '', password: '', confirm: '' });
  const [loading, setLoading] = useState(false);
  const [accepted, setAccepted] = useState(false);
  const navigate = useNavigate();

  const submit = async (e) => {
    e.preventDefault();
    if (!accepted) { toast.error('Devi accettare l\'informativa privacy'); return; }
    if (form.password !== form.confirm) { toast.error('Le password non coincidono'); return; }
    if (form.password.length < 8) { toast.error('La password deve avere almeno 8 caratteri'); return; }
    setLoading(true);
    try {
      const res = await authApi.register({
        nome: form.nome, cognome: form.cognome, email: form.email,
        telefono: form.telefono, password: form.password,
      });
      persistSession(res.data.token, res.data.user);
      toast.success('Account creato, benvenuto!');
      navigate('/clienti', { replace: true });
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Errore durante la registrazione');
    } finally {
      setLoading(false);
    }
  };

  const upd = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  return (
    <Shell title="Crea il tuo account" subtitle="Gratis, nessuna carta richiesta" data_testid="portal-register-page">
      <form onSubmit={submit} className="space-y-3">
        <div className="grid grid-cols-2 gap-3">
          <div>
            <Label className="text-slate-300 text-sm">Nome</Label>
            <InputIcon Icon={User} value={form.nome} onChange={upd('nome')} data-testid="reg-nome" required />
          </div>
          <div>
            <Label className="text-slate-300 text-sm">Cognome</Label>
            <InputIcon Icon={User} value={form.cognome} onChange={upd('cognome')} data-testid="reg-cognome" required />
          </div>
        </div>
        <div>
          <Label className="text-slate-300 text-sm">Email</Label>
          <InputIcon Icon={Mail} type="email" value={form.email} onChange={upd('email')} data-testid="reg-email" required />
        </div>
        <div>
          <Label className="text-slate-300 text-sm">Telefono (opzionale)</Label>
          <InputIcon Icon={Phone} value={form.telefono} onChange={upd('telefono')} data-testid="reg-telefono" />
        </div>
        <div>
          <Label className="text-slate-300 text-sm">Password</Label>
          <InputIcon Icon={Lock} type="password" value={form.password} onChange={upd('password')} data-testid="reg-password" required />
          <div className="text-[11px] text-slate-500 mt-1">Almeno 8 caratteri</div>
        </div>
        <div>
          <Label className="text-slate-300 text-sm">Conferma password</Label>
          <InputIcon Icon={Lock} type="password" value={form.confirm} onChange={upd('confirm')} data-testid="reg-confirm" required />
        </div>
        <label className="flex items-start gap-2 pt-2 cursor-pointer">
          <input type="checkbox" checked={accepted} onChange={(e) => setAccepted(e.target.checked)} data-testid="reg-privacy"
            className="mt-1 w-4 h-4 rounded border-white/20 bg-slate-950 accent-sky-400" />
          <span className="text-xs text-slate-400 leading-relaxed">
            Accetto l'<Link to="/privacy" className="text-sky-300 underline">informativa privacy</Link> e il trattamento dei miei dati.
          </span>
        </label>
        <Button type="submit" disabled={loading} data-testid="reg-submit"
          className="w-full h-11 bg-sky-500 hover:bg-sky-400 text-white font-semibold">
          {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : (<>Crea account <ArrowRight className="w-4 h-4 ml-2" /></>)}
        </Button>
        <div className="text-center text-sm text-slate-400">
          Hai già un account?{' '}
          <Link to="/clienti/login" className="text-sky-300 underline">Accedi</Link>
        </div>
      </form>
    </Shell>
  );
};

// --------- FORGOT ---------
export const PortalForgot = () => {
  const [email, setEmail] = useState('');
  const [done, setDone] = useState(false);
  const [loading, setLoading] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    if (!email) return;
    setLoading(true);
    try {
      await authApi.forgot({ email });
      setDone(true);
    } catch (err) {
      // uniforme per sicurezza
      setDone(true);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Shell title="Password dimenticata" subtitle="Ti inviamo un link via email" data_testid="portal-forgot-page">
      {done ? (
        <div className="text-center space-y-4">
          <div className="w-14 h-14 mx-auto rounded-full bg-emerald-500/15 border border-emerald-400/30 grid place-items-center">
            <CheckCircle2 className="w-7 h-7 text-emerald-300" />
          </div>
          <div className="text-slate-200">Se l'email è nel nostro sistema, riceverai le istruzioni entro pochi minuti.</div>
          <Link to="/clienti/login" className="text-sky-300 underline text-sm">Torna al login</Link>
        </div>
      ) : (
        <form onSubmit={submit} className="space-y-4">
          <div>
            <Label className="text-slate-300 text-sm">Email</Label>
            <InputIcon Icon={Mail} type="email" value={email} onChange={(e) => setEmail(e.target.value)} data-testid="forgot-email" required />
          </div>
          <Button type="submit" disabled={loading} data-testid="forgot-submit"
            className="w-full h-11 bg-sky-500 hover:bg-sky-400 text-white font-semibold">
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Invia link reset'}
          </Button>
          <div className="text-center text-sm text-slate-400">
            Ti sei ricordato?{' '}
            <Link to="/clienti/login" className="text-sky-300 underline">Torna al login</Link>
          </div>
        </form>
      )}
    </Shell>
  );
};

// --------- RESET ---------
export const PortalReset = () => {
  const [params] = useSearchParams();
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const token = params.get('token') || '';
  const navigate = useNavigate();

  const submit = async (e) => {
    e.preventDefault();
    if (!token) { toast.error('Link non valido'); return; }
    if (password !== confirm) { toast.error('Le password non coincidono'); return; }
    if (password.length < 8) { toast.error('Minimo 8 caratteri'); return; }
    setLoading(true);
    try {
      await authApi.reset({ token, new_password: password });
      setDone(true);
      setTimeout(() => navigate('/clienti/login', { replace: true }), 1800);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Token non valido o scaduto');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Shell title="Reimposta password" subtitle="Scegli una nuova password sicura" data_testid="portal-reset-page">
      {done ? (
        <div className="text-center space-y-3">
          <div className="w-14 h-14 mx-auto rounded-full bg-emerald-500/15 border border-emerald-400/30 grid place-items-center">
            <CheckCircle2 className="w-7 h-7 text-emerald-300" />
          </div>
          <div className="text-slate-200">Password aggiornata. Ti porto al login...</div>
        </div>
      ) : (
        <form onSubmit={submit} className="space-y-4">
          <div>
            <Label className="text-slate-300 text-sm">Nuova password</Label>
            <InputIcon Icon={Lock} type="password" value={password} onChange={(e) => setPassword(e.target.value)} data-testid="reset-password" required />
          </div>
          <div>
            <Label className="text-slate-300 text-sm">Conferma password</Label>
            <InputIcon Icon={Lock} type="password" value={confirm} onChange={(e) => setConfirm(e.target.value)} data-testid="reset-confirm" required />
          </div>
          <Button type="submit" disabled={loading} data-testid="reset-submit"
            className="w-full h-11 bg-sky-500 hover:bg-sky-400 text-white font-semibold">
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Imposta nuova password'}
          </Button>
        </form>
      )}
    </Shell>
  );
};

// ------- PROTECTED ROUTE -------
export const RequirePortalAuth = ({ children }) => {
  const token = typeof window !== 'undefined' ? localStorage.getItem(TOKEN_KEY) : null;
  const location = useLocation();
  if (!token) return <PortalLoginRedirect from={location} />;
  return children;
};

const PortalLoginRedirect = () => {
  const navigate = useNavigate();
  useEffect(() => { navigate('/clienti/login', { replace: true }); }, [navigate]);
  return null;
};

export { TOKEN_KEY, USER_KEY };
